import { Component, ErrorInfo, ReactNode } from 'react'
import { captureException } from '@/lib/sentry'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * Global error boundary that catches JavaScript errors anywhere in the child
 * component tree and displays a fallback UI instead of crashing the app.
 *
 * Usage:
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo })

    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    // Send to Sentry (only in production when DSN is configured)
    captureException(error, {
      componentStack: errorInfo.componentStack,
    })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return <ErrorFallback error={this.state.error} onReset={this.handleReset} onReload={this.handleReload} />
    }

    return this.props.children
  }
}

interface ErrorFallbackProps {
  error: Error | null
  onReset: () => void
  onReload: () => void
}

function ErrorFallback({ error, onReset, onReload }: ErrorFallbackProps) {
  const isDev = import.meta.env.DEV

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-surface rounded-lg shadow-xl border border-border p-6 text-center">
        {/* Error Icon */}
        <div className="mx-auto w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Error Message */}
        <h1 className="text-xl font-semibold text-foreground mb-2">Something went wrong</h1>
        <p className="text-muted mb-6">
          An unexpected error occurred. This has been logged and we'll look into it.
        </p>

        {/* Dev-only error details */}
        {isDev && error && (
          <div className="mb-6 text-left">
            <details className="bg-background rounded border border-border p-3">
              <summary className="text-sm font-medium text-foreground cursor-pointer">
                Error details (dev only)
              </summary>
              <pre className="mt-2 text-xs text-red-400 overflow-auto max-h-48 p-2 bg-black/20 rounded">
                {error.message}
                {'\n\n'}
                {error.stack}
              </pre>
            </details>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 justify-center">
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-foreground bg-surface border border-border rounded-md hover:bg-surface-hover transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={onReload}
            className="px-4 py-2 text-sm font-medium text-white bg-accent rounded-md hover:bg-accent-hover transition-colors"
          >
            Reload Page
          </button>
        </div>

        {/* Support Link */}
        <p className="mt-6 text-xs text-muted">
          If this keeps happening, please{' '}
          <a
            href="https://github.com/troyjr4103/neuro-ha-ecosystem/issues"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline"
          >
            report an issue
          </a>
          .
        </p>
      </div>
    </div>
  )
}

export default ErrorBoundary
